import dayjs from 'dayjs';
import { ContactHistoryType } from '@leedpulse-monorepo/enums';
import { ContactHistoryItem } from '../../../../domain/models/Contact';

export type ContactExchangeKind = 'sms' | 'call' | 'voicemail' | 'receptionist';
export type ContactExchangesFilter = 'all' | 'calls' | 'sms' | 'receptionist';

export const CONTACT_EXCHANGES_FILTERS: { value: ContactExchangesFilter; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'calls', label: 'Appels' },
  { value: 'sms', label: 'SMS' },
  { value: 'receptionist', label: 'Réceptionniste' },
];

export interface ContactExchange {
  key: string;
  kind: ContactExchangeKind;
  title: string;
  text: string;
  timestamp: number;
  isMissed: boolean;
}

export interface ContactExchangesDay {
  timestamp: number;
  exchanges: ContactExchange[];
}

const FILTER_KINDS: Record<Exclude<ContactExchangesFilter, 'all'>, ContactExchangeKind[]> = {
  calls: ['call', 'voicemail'],
  sms: ['sms'],
  receptionist: ['receptionist'],
};

const SMS_TITLES: Partial<Record<string, string>> = {
  [ContactHistoryType.messagereceived]: 'SMS reçu',
  [ContactHistoryType.messagesent]: 'SMS envoyé',
  [ContactHistoryType.whatsappreceived]: 'WhatsApp reçu',
  [ContactHistoryType.whatsappsent]: 'WhatsApp envoyé',
};

// The history mixes exchanges with events (qualification, tags...): only the exchanges are kept
function toContactExchange(item: ContactHistoryItem, index: number): ContactExchange | null {
  const info = item.info?.trim() ?? '';
  const base = { key: `${item.timestamp}-${index}`, timestamp: item.timestamp, text: info, isMissed: false };

  const smsTitle = SMS_TITLES[item.type];
  if (smsTitle) return { ...base, kind: 'sms', title: smsTitle };

  switch (item.type) {
    case ContactHistoryType.call: {
      // A missed call is only told by its info, like on mobile (TimelineItem)
      const isMissed = info.toLowerCase().includes('manqué');
      return { ...base, kind: 'call', title: isMissed ? 'Appel manqué' : 'Appel', text: isMissed ? '' : info, isMissed };
    }
    case ContactHistoryType.voicemailreceived:
      return { ...base, kind: 'voicemail', title: 'Message vocal' };
    case ContactHistoryType.receptionist: {
      // "<Assistant> a répondu · <durée>\n<résumé>"
      const [title, ...summary] = info.split('\n');
      return { ...base, kind: 'receptionist', title: title || 'Réceptionniste IA a répondu', text: summary.join('\n').trim() };
    }
    default:
      return null;
  }
}

export function buildContactExchanges(history: ContactHistoryItem[]): ContactExchange[] {
  return history
    .map(toContactExchange)
    .filter((exchange): exchange is ContactExchange => !!exchange)
    .sort((a, b) => b.timestamp - a.timestamp);
}

export function filterContactExchanges(exchanges: ContactExchange[], filter: ContactExchangesFilter): ContactExchange[] {
  if (filter === 'all') return exchanges;
  return exchanges.filter((exchange) => FILTER_KINDS[filter].includes(exchange.kind));
}

export function groupContactExchangesByDay(exchanges: ContactExchange[]): ContactExchangesDay[] {
  const days: ContactExchangesDay[] = [];
  exchanges.forEach((exchange) => {
    const lastDay = days[days.length - 1];
    if (lastDay && dayjs(lastDay.timestamp).isSame(exchange.timestamp, 'day')) {
      lastDay.exchanges.push(exchange);
    } else {
      days.push({ timestamp: exchange.timestamp, exchanges: [exchange] });
    }
  });
  return days;
}
